/**
 * scheduleTime — the two conversions between a schedule's local wall-clock
 * time and the UTC instant the API stores.
 *
 * A job's start and end times are entered as HH:MM in a timezone the user
 * picks (their profile timezone by default), and stored as UTC ISO 8601
 * strings. Every surface that edits them has to convert on the way out and
 * on the way back in, and a second copy of the DST arithmetic is exactly the
 * kind of thing that drifts by an hour twice a year. One implementation.
 *
 * Both directions go through Intl.DateTimeFormat#formatToParts and Date.UTC,
 * never through `new Date(<local string>)`. The copy this replaced parsed
 * the typed date+time with `new Date('YYYY-MM-DDTHH:MM:00')`, which the
 * spec reads in the BROWSER's zone, and then subtracted the chosen zone's
 * offset on top — so on any machine not running in UTC the stored instant
 * was off by the browser's own offset (14:00 Eastern saved as 22:00Z from a
 * laptop in New York). Nothing here depends on where the machine is.
 */

/** The wall-clock reading of an instant in a zone, as numbers. */
function wallClock(instantMs: number, tz: string): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
	const parts = new Intl.DateTimeFormat('en-US', {
		timeZone: tz,
		hourCycle: 'h23',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	}).formatToParts(new Date(instantMs));
	const read = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((p) => p.type === type)?.value ?? 0);
	return { year: read('year'), month: read('month'), day: read('day'), hour: read('hour'), minute: read('minute'), second: read('second') };
}

/** The zone's offset from UTC at that instant, in ms — e.g. -4h for EDT. */
function zoneOffsetMs(instantMs: number, tz: string): number {
	const w = wallClock(instantMs, tz);
	return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second) - instantMs;
}

/**
 * Converts a local date (YYYY-MM-DD) and time (HH:MM) in the given IANA
 * timezone to a UTC ISO 8601 string suitable for the API.
 *
 * Reads the wall clock as if it were UTC, then removes the zone's offset.
 * The offset is looked up twice — once at that first guess, once at the
 * resulting instant — so a DST switch between the two is honoured.
 * Returns '' when either part is missing or unparsable.
 */
export function localToUtcIso(date: string, time: string, tz: string): string {
	if (!date || !time) return '';
	const [year, month, day] = date.split('-').map(Number);
	const [hour, minute] = time.split(':').map(Number);
	const naiveUtc = Date.UTC(year, month - 1, day, hour, minute);
	if (Number.isNaN(naiveUtc)) return '';

	const firstGuess = naiveUtc - zoneOffsetMs(naiveUtc, tz);
	const instant = naiveUtc - zoneOffsetMs(firstGuess, tz);
	return new Date(instant).toISOString();
}

/**
 * The inverse: a stored UTC instant back to the HH:MM an `<input type="time">`
 * wants, read in the given timezone. Returns '' for a missing or unparsable
 * value so the input simply renders empty.
 */
export function utcIsoToLocalTime(iso: string | null | undefined, tz: string): string {
	if (!iso) return '';
	const dt = new Date(iso);
	if (Number.isNaN(dt.getTime())) return '';
	const w = wallClock(dt.getTime(), tz);
	return `${String(w.hour).padStart(2, '0')}:${String(w.minute).padStart(2, '0')}`;
}
